import { Component } from "react";

/**
 * App-wide error boundary. Catches any uncaught render-time error in the React
 * tree and shows a clear recovery UI instead of a blank/black page.
 * Critical for production: a runtime crash in a deeply-nested component (e.g.
 * a third-party SDK, a malformed API response) used to render an unrecoverable
 * dark page. This boundary makes the failure visible AND recoverable.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Surface to console for debugging; ops can wire this to Sentry later.
    if (typeof window !== "undefined" && window.console) {
      window.console.error("[ErrorBoundary]", error, info);
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const message = error?.message || String(error);
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#050505]" data-testid="error-boundary">
        <div className="max-w-xl w-full border border-[#FF3333]/40 bg-[#FF3333]/5 p-8 md:p-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF3333] font-mono mb-3">
            CRITICAL · RUNTIME ERROR
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-none">
            Something broke on this page.
          </h1>
          <p className="text-white/65 mt-4 text-sm leading-relaxed">
            We logged the error. You&apos;re not stuck — refresh this page or head back to your dashboard.
          </p>
          <pre className="mt-5 text-[11px] text-white/50 font-mono bg-black/40 p-3 overflow-auto max-h-32 border border-white/10" data-testid="error-boundary-message">
            {message}
          </pre>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="error-boundary-refresh"
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Refresh page
            </button>
            <a
              href="/dashboard"
              data-testid="error-boundary-dashboard"
              className="btn-secondary"
            >
              Back to dashboard
            </a>
            <a
              href="mailto:info@pivotproof.org?subject=Bug%20report%20%2F%20PivotProof"
              data-testid="error-boundary-support"
              className="btn-secondary"
            >
              Email support
            </a>
          </div>
        </div>
      </div>
    );
  }
}
